/* METALYSER tax ID v1. Format validation only, not identity or AEAT validation.
   Theme-side protection; cannot enforce checkout server-side on its own. */
(() => {
  'use strict';
  if (window.MetalyserTaxId) return;
  const KEY = 'DNI/NIE/NIF/CIF';
  const fields = new Set();
  let value = null, revision = 0, locked = false, ready = false;
  let message = '', hasError = false, tail = Promise.resolve();
  const normalize = input => String(input ?? '').toUpperCase().replace(/[\s.\-]/g, '');
  const valid = input => /^(?:[0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z]|[KLM][A-Z0-9]{7}[A-Z]|[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J])$/.test(normalize(input));
  const invalidMessage = input => normalize(input)
    ? 'Revisa el formato: 9 caracteres de un DNI, NIE o NIF español, incluida su letra de identificación o control.'
    : 'Introduce tu DNI, NIE, NIF o CIF para continuar.';

  function paint() {
    fields.forEach(field => {
      const input = field.querySelector('[data-mt-input]');
      if (!input) return;
      if (input.value !== value) input.value = value ?? '';
      input.readOnly = locked;
      input.setAttribute('aria-invalid', String(hasError));
      // Network errors do not invalidate a syntactically correct identifier.
      input.setCustomValidity(valid(input.value) ? '' : invalidMessage(input.value));
      const status = field.querySelector('[data-mt-status]');
      if (status) {
        status.textContent = message;
        status.toggleAttribute('data-error', hasError);
      }
      field.querySelector('[data-mt-unavailable]')?.toggleAttribute('hidden', ready);
    });
  }
  function status(text, error = false) { message = text; hasError = error; paint(); }
  function update(next) {
    if (next !== value) { value = next; revision++; }
    paint();
  }
  function focus(form) {
    const own = form && Array.from(form.elements).find(el => el.matches('[data-mt-input]'));
    const input = own?.getClientRects().length ? own : Array.from(fields)
      .map(field => field.querySelector('[data-mt-input]')).find(el => el?.getClientRects().length);
    input?.scrollIntoView({ block: 'center', behavior: 'auto' });
    input?.focus({ preventScroll: true });
  }
  function enableButtons() {
    if (!ready) return;
    document.querySelectorAll('[data-mt-checkout][data-mt-initial-disabled]').forEach(button => {
      button.removeAttribute('data-mt-initial-disabled');
      button.disabled = false;
    });
  }
  function persist(next) {
    // Serialize writes so an earlier blur/clear cannot overwrite the checkout save.
    const job = tail.catch(() => {}).then(async () => {
      const root = window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(root.replace(/\/?$/, '/') + 'cart/update.js', {
          method: 'POST', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          // Only update our key: preserve notes, delivery, gift wrapping and app attributes.
          body: JSON.stringify({ attributes: { [KEY]: next } })
        });
        const cart = await response.json();
        if (!response.ok || cart.errors || cart.status >= 400 ||
            String(cart.attributes?.[KEY] ?? '') !== next) throw new Error('save');
        return cart;
      } catch (_) {
        throw new Error('No se ha podido guardar el identificador fiscal. Revisa tu conexión y vuelve a intentarlo.');
      } finally { clearTimeout(timeout); }
    });
    tail = job.catch(() => {});
    return job;
  }
  async function onChange(event) {
    if (locked) return;
    update(normalize(event.target.value));
    const version = revision;
    const accepted = valid(value);
    status(accepted ? 'Guardando…' : invalidMessage(value), !accepted);
    try {
      // Clear a previously saved ID when the customer erases it or enters an invalid format.
      await persist(accepted ? value : '');
      if (revision === version && !locked && accepted) status('Identificador fiscal guardado.');
    } catch (error) {
      if (revision === version && !locked) status(error.message, true);
    }
  }
  class TaxIdField extends HTMLElement {
    connectedCallback() {
      // Liquid section HTML is complete by the end of the current microtask.
      queueMicrotask(() => {
        if (!this.isConnected) return;
        const input = this.querySelector('[data-mt-input]');
        if (!input) return;
        fields.add(this);
        if (value === null) value = normalize(input.value);
        if (!this.bound) {
          this.bound = true;
          input.addEventListener('input', () => {
            if (locked) { paint(); return; }
            message = ''; hasError = false;
            update(input.value);
          });
          input.addEventListener('change', onChange);
        }
        paint(); enableButtons();
      });
    }
    disconnectedCallback() { fields.delete(this); }
  }
  window.MetalyserTaxId = {
    normalize, valid,
    enableCheckout() { ready = true; paint(); enableButtons(); },
    lock(form) {
      const input = form && Array.from(form.elements).find(el => el.matches('[data-mt-input]'));
      if (!input) { status('Recarga el carrito para completar tus datos fiscales.', true); focus(form); return false; }
      update(normalize(input.value));
      if (!valid(value)) { status(invalidMessage(value), true); focus(form); return false; }
      locked = true; status('Comprobando el pedido…');
      return true;
    },
    async save(form) {
      if (!locked || !valid(value)) throw new Error('Revisa el identificador fiscal antes de pagar.');
      try {
        const cart = await persist(value);
        status('Identificador fiscal guardado.');
        return cart;
      } catch (error) { status(error.message, true); focus(form); throw error; }
    },
    unlock() { locked = false; paint(); }
  };
  customElements.define('metalyser-tax-id', TaxIdField);
  // Section refreshes may replace only the checkout button, without the input.
  new MutationObserver(records => {
    if (records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 &&
      (node.matches('[data-mt-checkout]') || node.querySelector('[data-mt-checkout]'))))) enableButtons();
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('pageshow', () => { locked = false; paint(); enableButtons(); });
})();
